// FileMaker owns invoice issuance. A generated ZIP is only a handoff artifact.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Download, PackageOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { authHeaders } from "@/lib/api";
import { isStaff } from "@/lib/auth";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { useAllocationsText, allocationsFormat as fmt } from "@/lib/inventory/allocationsI18n";

interface ExportRow { export_id: string; created_at: string | null; kind: string; rows: number; status: string }
interface Notice { order_id: string; code?: string; detail: string }
type ExportResult = ({ status: "generated"; export_id: string; rows: number; lines: number }
  | { status: "empty" | "blocked"; export_id: null; rows: 0; lines?: 0 }) & { blocked?: Notice[]; warnings?: Notice[] };
interface ExportHistory { batches: ExportRow[]; pending_orders: number }
type Failure = { kind: "invalid" } | { kind: "http"; status: number; sourceDetail: string } | { kind: "source"; sourceDetail: string };
interface ExportError { source: "read" | "history" | "post" | "download"; failure: Failure }
class CheckedJsonError extends Error {
  constructor(readonly failure: Failure) { super(failure.kind); }
}
function failureOf(error: unknown): Failure {
  return error instanceof CheckedJsonError ? error.failure : { kind: "source", sourceDetail: String(error) };
}
async function checkedJson(r: Response): Promise<unknown> {
  let body: unknown;
  try { body = await r.json(); } catch { if (r.ok) throw new CheckedJsonError({ kind: "invalid" }); }
  if (!r.ok) {
    const sourceDetail = body && typeof body === "object" && "detail" in body ? String(body.detail) : "";
    throw new CheckedJsonError({ kind: "http", status: r.status, sourceDetail });
  }
  return body;
}
function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function count(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function notices(value: unknown): value is Notice[] | undefined {
  return value === undefined || Array.isArray(value) && value.every(n => record(n) && typeof n.order_id === "string" && typeof n.detail === "string" && (n.code === undefined || typeof n.code === "string"));
}
function principalPart(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0 && !/[\u0000-\u001f\u007f]/.test(value);
}
function exportHistory(value: unknown): value is ExportHistory {
  return record(value) && count(value.pending_orders) && Array.isArray(value.batches) && value.batches.every(row =>
    record(row) && principalPart(row.export_id) && (row.created_at === null || typeof row.created_at === "string")
    && typeof row.kind === "string" && typeof row.status === "string" && count(row.rows));
}
function exportResult(value: unknown): value is ExportResult {
  if (!record(value) || !notices(value.blocked) || !notices(value.warnings)) return false;
  if (value.status === "generated") return principalPart(value.export_id) && count(value.rows) && value.rows > 0 && count(value.lines);
  // A no-artifact outcome cannot claim exported orders or lines. The real API
  // omits lines (and sometimes warnings); absence is not a malformed response.
  return (value.status === "empty" || value.status === "blocked") && value.export_id === null && value.rows === 0
    && (value.lines === undefined || value.lines === 0);
}
export function AdminExports() {
  const auth = useAuth();
  const t = useAllocationsText();
  if (!auth.isReady) return <p role="status">{t.loading}</p>;
  const { issuer, sub } = auth.user ?? {};
  if (!auth.isAuthenticated || !isStaff(auth.accountType) || !principalPart(issuer) || !principalPart(sub)) return <p role="alert">{t.denied}</p>;
  // A principal/role transition replaces private state before the next committed paint.
  return <ExportWorkspace key={JSON.stringify([issuer, sub, auth.accountType])} canRun={auth.accountType === "admin"} />;
}
function ExportWorkspace({ canRun }: { canRun: boolean }) {
  const t = useAllocationsText();
  const [batches, setBatches] = useState<ExportRow[]>([]);
  const [pending, setPending] = useState(0);
  const [running, setRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<ExportError | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const busy = useRef(false);
  const active = useRef(false);
  const requests = useRef(new Set<AbortController>());
  const reads = useRef({ sequence: 0, epoch: 0, pending: false });
  const [refreshing, setRefreshing] = useState(false);
  const message = !result ? "" : result.status === "generated"
    ? fmt(t.generated, { id: result.export_id!, rows: result.rows!, lines: result.lines! })
    : result.status === "blocked" ? t.blockedExport : t.emptyExport;
  const failure = error?.failure;
  const errorDetail = !failure ? "" : failure.kind === "invalid" ? t.invalidResponse : failure.kind === "http"
    ? `HTTP ${failure.status}${failure.sourceDetail ? `: ${failure.sourceDetail}` : ""}` : failure.sourceDetail;
  const errorText = !error ? "" : `${error.source === "history" ? t.history : error.source === "read" ? t.failed : error.source === "post" ? t.exportFailed : t.downloadFailed} ${errorDetail}`;

  // Read generations also belong to the current mutation epoch. An older read
  // cannot clear a newer failure lock, including after a successful POST.
  const refresh = useCallback(async (source: "read" | "history" = "read") => {
    if (!active.current || (busy.current && source === "read")) return;
    const sequence = ++reads.current.sequence, epoch = reads.current.epoch;
    const current = () => active.current && sequence === reads.current.sequence && epoch === reads.current.epoch;
    const controller = new AbortController(); requests.current.add(controller);
    reads.current.pending = true; setRefreshing(true); setError(null);
    try {
      const data = await checkedJson(await fetch("/api/exports", { credentials: "include", headers: authHeaders(), signal: controller.signal }));
      if (!current()) return;
      if (!exportHistory(data)) throw new CheckedJsonError({ kind: "invalid" });
      setBatches(data.batches); setPending(data.pending_orders); setLoaded(true); setBlocked(false);
    } catch (e) {
      if (current()) {
        reads.current.epoch++; reads.current.pending = false; setRefreshing(false);
        setError({ source, failure: failureOf(e) }); setBlocked(true);
      }
    } finally {
      requests.current.delete(controller);
      if (current()) { reads.current.pending = false; setRefreshing(false); }
    }
  }, []);
  useLayoutEffect(() => {
    active.current = true;
    const controllers = requests.current;
    return () => {
      active.current = false; reads.current.epoch++; reads.current.pending = false;
      for (const controller of controllers) controller.abort();
      controllers.clear();
    };
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function downloadExport(exportId: string) {
    if (!active.current) return;
    const epoch = reads.current.epoch;
    const current = () => active.current && epoch === reads.current.epoch;
    const controller = new AbortController(); requests.current.add(controller);
    try {
      const r = await fetch(`/api/exports/${encodeURIComponent(exportId)}/download`, { credentials: "include", headers: authHeaders(), signal: controller.signal });
      if (!current()) return;
      if (!r.ok) { await checkedJson(r); return; }
      const blob = await r.blob();
      if (!current()) return;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a"); a.href = url;
        a.download = `${exportId.replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 100) || "export"}.zip`;
        a.click();
      } finally { URL.revokeObjectURL(url); }
    } catch (e) { if (current()) setError({ source: "download", failure: failureOf(e) }); }
    finally { requests.current.delete(controller); }
  }
  async function runExport() {
    if (!active.current || !canRun || busy.current || reads.current.pending || blocked || !loaded || pending === 0) return;
    const epoch = ++reads.current.epoch;
    const current = () => active.current && epoch === reads.current.epoch;
    const controller = new AbortController(); requests.current.add(controller);
    busy.current = true; setRunning(true); setLoaded(false); setError(null); setResult(null);
    try {
      const data = await checkedJson(await fetch("/api/exports/run", { method: "POST", credentials: "include", headers: authHeaders(), signal: controller.signal }));
      if (!current()) return;
      if (!exportResult(data)) throw new CheckedJsonError({ kind: "invalid" });
      setResult(data);
      await refresh("history");
    } catch (e) {
      if (current()) { reads.current.epoch++; setError({ source: "post", failure: failureOf(e) }); setBlocked(true); }
    } finally {
      requests.current.delete(controller);
      if (active.current) { busy.current = false; setRunning(false); }
    }
  }
  return <div className="space-y-4">
    <Card><CardHeader>
      <CardTitle className="flex items-center gap-2"><PackageOpen className="h-5 w-5" />{t.exportTitle}</CardTitle>
      <CardDescription>{t.exportIntro}</CardDescription>
    </CardHeader><CardContent className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {canRun ? <Button onClick={() => void runExport()} disabled={running || refreshing || blocked || !loaded || pending === 0}>{running ? t.generating : fmt(t.run, { count: pending })}</Button> : <p>{t.financeOnly}</p>}
        <Button variant="secondary" disabled={running} onClick={() => { setError(null); void refresh(); }}>{t.refresh}</Button>
        {loaded && pending === 0 && <span>{t.noPending}</span>}
      </div>
      {error && <p role="alert">{errorText}</p>}
      {message && <p role="status">{message}</p>}
      {!!result?.blocked?.length && <section aria-label={t.blockedOrders}><h3>{t.blockedOrders}</h3><ul>{result.blocked.map((n, i) => <li key={i}><code>{n.order_id}</code> — <code>{n.code}</code> — <span>{n.detail}</span></li>)}</ul></section>}
      {!!result?.warnings?.length && <section aria-label={t.warnings}><h3>{t.warnings}</h3><ul>{result.warnings.map((n, i) => <li key={i}><code>{n.order_id}</code> — <span>{n.detail}</span></li>)}</ul></section>}
    </CardContent></Card>
    <Card><CardHeader><CardTitle>{t.history}</CardTitle></CardHeader><CardContent>
      <table className="w-full text-left text-sm"><thead><tr className="border-b border-border">
        <th>{t.export}</th><th>{t.created}</th><th>{t.kind}</th><th>{t.orders}</th><th>{t.status}</th><th>{t.download}</th>
      </tr></thead><tbody>{batches.map(b => <tr key={b.export_id} className="border-b border-border/50">
        <td className="font-mono">{b.export_id}</td><td>{b.created_at?.slice(0, 19).replace("T", " ") ?? "—"}</td><td>{b.kind}</td><td>{b.rows}</td><td>{b.status}</td>
        <td><Button variant="ghost" onClick={() => void downloadExport(b.export_id)}><Download className="h-4 w-4" />{t.download}</Button></td>
      </tr>)}{loaded && !batches.length && <tr><td colSpan={6}>{t.noExports}</td></tr>}</tbody></table>
    </CardContent></Card>
  </div>;
}
